import type { ApiResponse } from '../_types/api'

const PRODUCTION_OPEN_ENDPOINT = 'https://api.quickcasa.ai/v1/open'

/**
 * Resolves which Open endpoint URL this build should target. Reads from the
 * `VITE_OPEN_ENDPOINT` env variable (set in `.env.development` /
 * `.env.production`) and falls back to the production URL when nothing is
 * configured. Logged once at module load to make misconfigurations obvious in
 * the browser console.
 *
 * @returns {string} The base URL for the Open endpoint.
 */
const resolveOpenEndpoint = (): string => {
  const fromEnvironment = import.meta.env.VITE_OPEN_ENDPOINT
  if (typeof fromEnvironment === 'string' && fromEnvironment.length > 0) {
    return fromEnvironment
  }
  return PRODUCTION_OPEN_ENDPOINT
}

const OPEN_ENDPOINT = resolveOpenEndpoint()

if (import.meta.env.DEV) {
  console.info('[book] doOpenCall target:', OPEN_ENDPOINT)
}

/**
 * Calls a public QuickCasa backend command via the `/v1/open` proxy. The
 * payload shape mirrors what the admin-portal's `doOpenCall` produces: the
 * command name and its fields are wrapped in a top-level `data` object, which
 * is the Firebase httpsCallable convention the upstream `onRequest` function
 * is built to unwrap.
 *
 * Throws on network errors. Non-200 `ApiResponse.code` values are NOT thrown —
 * callers inspect the result themselves so they can render the right UI state
 * (e.g. 404 = "Event type not found", 409 = "slot just got taken").
 *
 * @param {string} command - Backend command name, e.g. `calendar-geteventtypebyslug`.
 * @param {Record<string, unknown>} payload - Additional fields forwarded to the command.
 * @returns {Promise<ApiResponse>} The parsed API response.
 */
const doOpenCall = async (
  command: string,
  payload: Record<string, unknown> = {},
): Promise<ApiResponse> => {
  try {
    const response = await fetch(OPEN_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        data: {
          command,
          ...payload,
        },
      }),
    })

    let parsed: ApiResponse
    try {
      parsed = (await response.json()) as ApiResponse
    } catch {
      return {
        code: response.status,
        message: `Open endpoint returned HTTP ${response.status}`,
        data: '',
      }
    }

    // Non-2xx still carries a structured ApiResponse (e.g. Turnstile 403).
    if (!response.ok && typeof parsed.code !== 'number') {
      return {
        code: response.status,
        message: `Open endpoint returned HTTP ${response.status}`,
        data: '',
      }
    }

    return parsed
  } catch (error) {
    return {
      code: 500,
      message: error instanceof Error ? error.message : 'Open call failed',
      data: '',
    }
  }
}

export { doOpenCall }
